import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getDuration, useReliableInterval } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import StatusBlock, { StatusBlockProps } from '@/components/elements/StatusBlock/StatusBlock';
import { convertSecondsToMinutes } from '@/utils/modifiers/convertSecondsToMinutes';

import { useSecurityStore } from '../stores';

enum MfaStatus {
  NONE = 'NONE',
  CLEAR = 'CLEAR',
  LOCKED = 'LOCKED',
}

const MfaStatusBlock: React.FC<Partial<StatusBlockProps>> = ({ ...props }) => {
  const username = useSecurityStore((state) => state.state.username);
  const requiredParticipantMfa = useSecurityStore((state) => state.state.requiredParticipantMfa);
  const isMfaLocked = useSecurityStore((state) => state.state.isMfaLocked);
  const mfaLockedTo = useSecurityStore((state) => state.state.mfaLockedTo);
  const setState = useSecurityStore((state) => state.setState);

  const mfaStatus = useMemo(() => {
    if (isMfaLocked) {
      return MfaStatus.LOCKED;
    }
  
    if (!requiredParticipantMfa || !username) {
      return MfaStatus.NONE;
    }

    if (requiredParticipantMfa && !isMfaLocked) {
      return MfaStatus.CLEAR;
    }
  }, [username, requiredParticipantMfa, isMfaLocked]);

  const secondsLeft = useMemo(() => {
    const mfaLockedToTime = dayjs(mfaLockedTo);
    const currentTime = dayjs(new Date().toISOString());
    return mfaLockedToTime.diff(currentTime, 'second');
  }, [mfaLockedTo]);

  const [remainingTime, setRemainingTime] = useState(secondsLeft);
  const [isTimerActive, setIsTimerActive] = useState(secondsLeft > 0);

  const updateRemainingTime = useCallback((value: number) => {
    const currentRemainingTime = value - 1;
    if (currentRemainingTime <= 0) {
      setState({ isMfaLocked: false });
      setIsTimerActive(false);
    }
    return currentRemainingTime;
  }, [setState]);

  const { start, stop } =  useReliableInterval(() => {
    setRemainingTime(updateRemainingTime);
  }, getDuration({ seconds: 1 }));

  useEffect(() => {
    if (isTimerActive) {
      start();
      return () => {
        stop();
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerActive]);

  useEffect(() => {
    if (!isMfaLocked) {
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMfaLocked]);

  return (
    <>
      {mfaStatus === MfaStatus.NONE && (
        <StatusBlock
          testId="mfa"
          title="None"
          backgroundColor="empty"
          {...props}
        />
      )}
      {mfaStatus === MfaStatus.CLEAR && (
        <StatusBlock
          testId="mfa"
          title="Clear"
          backgroundColor="success"
          {...props}
        />
      )}
      {mfaStatus === MfaStatus.LOCKED && (
        <StatusBlock
          testId="mfa"
          title={`Locked by MFA (${convertSecondsToMinutes(remainingTime)})`}
          backgroundColor="danger"
          {...props}
        />
      )}
    </>
  );
};

export default MfaStatusBlock;
